import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  PhoneIcon,
  CreditCardIcon,
  HeartIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import Topbar from '../PulseSurvey/Topbar';
import Pagination from '../../component/common/Pagination';
import ConfirmPopup from '../../component/common/modals/ConfirmPopup';
import OfferingCard from './OfferingCard';
import {
  getCompanyCustomOfferings,
  deleteCompanyCustomOffering,
  deleteCustomOffering,
  promoteCustomOfferingToTop
} from '../../api/offeringApi';
import { errorToast, successToast, isSuperAdmin } from '../../utils/helper';

const B2BOfferings = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const { slug } = userData;
  const isAdmin = isSuperAdmin();
  const [open, setOpen] = useState(false);
  const [customPerks, setCustomPerks] = useState([]);
  const [loadingPerks, setLoadingPerks] = useState(true);
  const [filteredPerks, setFilteredPerks] = useState([]);
  const [perkToDelete, setPerkToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionMenuId, setActionMenuId] = useState('');

  const handleDeletePerk = (perk) => {
    setPerkToDelete(perk);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!perkToDelete?._id && !perkToDelete?.id) return;

    setIsDeleting(true);
    try {
      const perkId = perkToDelete.id;
      const apiFunction = isAdmin ? deleteCustomOffering : deleteCompanyCustomOffering;
      const response = await apiFunction(perkId);

      if (response?.meta?.code === 200 || response?.status === 200) {
        successToast('Perk deleted successfully');
        setShowDeleteConfirm(false);
        setPerkToDelete(null);
        // Refresh perks list
        fetchCustomPerks();
      } else {
        errorToast(response?.message || 'Failed to delete perk');
      }
    } catch (error) {
      console.error('Error deleting perk:', error);
      errorToast(error?.message || 'Failed to delete perk');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPerkToDelete(null);
  };

  const handlePromoteToggle = async (offeringId) => {
    try {
      await promoteCustomOfferingToTop(offeringId);
      successToast('Perk promotion status updated successfully');
      // Refresh perks to get updated data
      fetchCustomPerks();
    } catch (error) {
      console.error('Error toggling promote:', error);
      errorToast('Failed to update promotion status');
      throw error;
    }
  };

  // Fetch custom perks on component mount
  useEffect(() => {
    fetchCustomPerks();
  }, []);

  // Filter perks
  useEffect(() => {
    setFilteredPerks(customPerks);
  }, [customPerks]);

  const fetchCustomPerks = async () => {
    setLoadingPerks(true);
    try {
      const response = await getCompanyCustomOfferings(
        {
          page: 1,
          limit: 100
        },
        true
      );
      if (response.meta.code === 200 && response.data) {
        setCustomPerks(response.data || []);
      } else {
        setCustomPerks([]);
      }
    } catch (error) {
      console.error('Error fetching custom perks:', error);
      errorToast('Failed to load custom perks');
      setCustomPerks([]);
    } finally {
      setLoadingPerks(false);
    }
  };

  const perkCards = [
    {
      id: 1,
      title: 'Instant Support (EAP)',
      description:
        'Employee Assistance Program with 24/7 counselling and support services for your workforce.',
      icon: PhoneIcon,
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
      borderColor: 'border-blue-200 dark:border-blue-800',
      route: `/${slug}/instant-support`
    },
    {
      id: 2,
      title: 'Salary Sacrifice',
      description:
        'Comprehensive salary sacrifice schemes with custom plans for employee benefits.',
      icon: CreditCardIcon,
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      iconColor: 'text-green-600 dark:text-green-400',
      borderColor: 'border-green-200 dark:border-green-800',
      route: `/${slug}/salary-sacrifice`
    },
    {
      id: 3,
      title: 'Health Scheme',
      description: 'Manage comprehensive health schemes and wellness programs for your employees.',
      icon: HeartIcon,
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
      borderColor: 'border-purple-200 dark:border-purple-800',
      route: `/${slug}/health-scheme`
    }
  ];

  const handleCardClick = (route) => {
    if (route) {
      navigate(route);
    }
  };

  return (
    <>
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>Perks | Shoorah Admin</title>
        </Helmet>
        <Topbar title={'Perks'} infoBtn={false} setOpen={setOpen} />

        {/* Cards Grid */}
        <div className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {perkCards.map((perk) => (
              <div
                key={perk.id}
                onClick={() => handleCardClick(perk.route)}
                className={`
                  relative p-6 rounded-lg border-2 ${perk.borderColor} ${perk.bgColor}
                  transition-all duration-300 hover:shadow-xl
                  ${perk.route ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed opacity-75'}
                `}
              >
                {/* Icon */}
                <div
                  className={`w-12 h-12 rounded-lg ${perk.bgColor} flex items-center justify-center mb-4`}
                >
                  <perk.icon className={`w-6 h-6 ${perk.iconColor}`} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {perk.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">{perk.description}</p>

                {/* Action Button */}
                {perk.route && (
                  <div className="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400">
                    <span>Manage</span>
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Custom Perks Section */}
        <div className="mt-12">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Custom Perks</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Manage all custom perks created for your organization
              </p>
            </div>
            {/* <button
              onClick={() => navigate(`/${slug}/offering/create`)}
              className="btn-primary"
            >
              Add Perk
            </button> */}
          </div>

          {loadingPerks ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading custom perks...</p>
              </div>
            </div>
          ) : customPerks.length === 0 ? (
            <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No custom perks yet. Create one to get started.
              </p>
              <button
                onClick={() => navigate(`/${slug}/offering/create`)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" />
                Create Custom Perk
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {filteredPerks.map((perk) => (
                    <OfferingCard
                      key={perk.id}
                      offering={perk}
                      actionMenuId={actionMenuId}
                      setActionMenuId={setActionMenuId}
                      onEdit={(perkItem) => {
                        navigate(`/${slug}/offering/create?id=${perkItem.id}`);
                      }}
                      onDelete={handleDeletePerk}
                      onManage={(perkItem) => {
                        navigate(`/${slug}/offering/manage/${perkItem.id}`);
                      }}
                      onPromoteToggle={handlePromoteToggle}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={handleConfirmDelete}
        handleNo={handleCancelDelete}
        message={`Are you sure you want to delete "${perkToDelete?.title}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </>
  );
};

export default B2BOfferings;
